import { useEffect, useMemo } from "react";
import useUIBp from "../../Hooks/useUIBoilerplate";
import useURLQuery from "../../Hooks/useURLQuery";
import { useWorker } from "../../Hooks/useWorker";
import getAllDevices, { devicesDataId } from "../../store/actions/devices/getAllDevices";
import getRootDevices, { usersListId } from "../../store/actions/devices/getRootDevices";
import { UIactions } from "../../store/slices/UI";
import { warehouseActions } from "../../store/slices/warehouse";
import { workerActions } from "../../store/slices/workers";
import { containsEscaped } from "../../utility/misc/filter";
import { sortObjByKey } from "../../utility/misc/sortObjByKey";
import { recursiveRequestId } from "../UI/BasicLayout/Components/FilterPlusActions/Recursive";
import { defaultFilter, defaultPagination, defaultPreFilter, defaultSelected, defaultSort } from "../UI/GeneralPurpose/genericIDs";
import CalculatePagination from "../UI/Utilities/CalculatePagination";
import { forceRescanId } from "./Actions/forceRescan";
import getC365List from "./Actions/getc365List";
import { requestLogsId } from "./Actions/requestLogs";
import Core from "./Components/Core";
import {
  connectionColumnID,
  devicesColumnID,
  groupColumnID,
  lastBackupColumnID,
  lastSentColumnID,
  nameColumnID,
  sizeColumnID,
  stateColumnID,
  uploadColumnID,
  versionColumnID,
} from "./Dialogs/ToggleReportColumns/Components/ColumnsList/ColumnsList";

export const devicesLoadedId = "devicesLoaded";

const Reports = () => {
  const { auth, dispatch, navigate, UI, warehouse } = useUIBp();
  const { group } = useURLQuery();
  const { done, working, error } = useWorker(devicesLoadedId);
  const prefilter = UI[defaultPreFilter];
  const recursive = UI[recursiveRequestId];
  const filter = UI[defaultFilter] ?? "";
  const sort = UI[defaultSort] ?? { value: "user" };
  const pagination = UI[defaultPagination] ?? {};

  useEffect(() => {
    dispatch(UIactions.setValue({ id: nameColumnID, value: true }));
    dispatch(UIactions.setValue({ id: devicesColumnID, value: true }));
    dispatch(UIactions.setValue({ id: groupColumnID, value: false }));
    dispatch(UIactions.setValue({ id: sizeColumnID, value: true }));
    dispatch(UIactions.setValue({ id: lastBackupColumnID, value: true }));
    dispatch(UIactions.setValue({ id: lastSentColumnID, value: true }));
    dispatch(UIactions.setValue({ id: uploadColumnID, value: false }));
    dispatch(UIactions.setValue({ id: versionColumnID, value: false }));
    dispatch(UIactions.setValue({ id: connectionColumnID, value: true }));
    dispatch(UIactions.setValue({ id: stateColumnID, value: true }));
  }, [dispatch, auth, group]);

  useEffect(() => {
    if (auth.admin !== auth.group) {
      navigate("/dashboard/files", { replace: true });
    } else if (!group && auth.admin) {
      navigate("/dashboard/reports?group=" + auth.group, { replace: true });
    }
  }, [navigate, auth, group]);

  useEffect(() => {
    if (prefilter) {
      dispatch(UIactions.setValue({ id: defaultFilter, value: prefilter }));
      dispatch(UIactions.clear(defaultPreFilter));
    }
  }, [dispatch, prefilter]);

  useEffect(() => {
    if (!done && !prefilter) {
      const controller = new AbortController();
      dispatch(warehouseActions.unload(devicesDataId));
      if (!recursive) {
        dispatch(getRootDevices({ ...auth, group, controller, includeEmpty: true }));
      } else {
        dispatch(getAllDevices({ ...auth, group, controller, includeEmpty: true }));
      }
      return () => {
        controller.abort();
      };
    }
  }, [dispatch, auth, group, recursive, done, prefilter]);

  const devices = useMemo(() => {
    const rawData = warehouse[devicesDataId];
    let devices = [];
    for (const key in rawData) {
      devices.push(rawData[key]);
    }
    return devices;
  }, [warehouse]);

  const users = useMemo(() => {
    return warehouse[usersListId] ?? [];
  }, [warehouse]);

  useEffect(() => {
    if (users !== 0 && done) {
      dispatch(getC365List({ ...auth, users }));
    }
  }, [dispatch, users, done, auth]);

  // console.log("devices", devices);
  // console.log("sort", sort);

  const filteredDevices = useMemo(() => {
    return devices
      .filter((dev) => containsEscaped(dev.user, filter) || containsEscaped(dev.name, filter) || containsEscaped(dev.croppedVersion, filter))
      .sort((a, b) => sortObjByKey(a, b, sort.value, sort.descending));
  }, [devices, filter, sort.value, sort.descending]);
  // console.log("filteredDevices", filteredDevices);

  // Array to select all
  const allDevices = useMemo(() => {
    return filteredDevices.filter((x) => !!x.mid).map((x) => x.mid);
  }, [filteredDevices]);

  // Initializing pagination
  useEffect(() => {
    dispatch(UIactions.setPagination({ count: filteredDevices.length }));
  }, [dispatch, filteredDevices.length]);

  const { from, to } = CalculatePagination(pagination);
  const paginatedDevices = useMemo(() => {
    return filteredDevices.filter((x, idx) => from <= idx + 1 && idx < to);
  }, [from, to, filteredDevices]);

  // Garbage collection
  useEffect(() => {
    return () => {
      dispatch(UIactions.clear(defaultFilter));
      dispatch(UIactions.clear(defaultPagination));
      dispatch(UIactions.clear(defaultSelected));
      dispatch(UIactions.clear(defaultSort));
      dispatch(UIactions.clear(nameColumnID));
      dispatch(UIactions.clear(devicesColumnID));
      dispatch(UIactions.clear(groupColumnID));
      dispatch(UIactions.clear(lastBackupColumnID));
      dispatch(UIactions.clear(lastSentColumnID));
      dispatch(UIactions.clear(sizeColumnID));
      dispatch(UIactions.clear(uploadColumnID));
      dispatch(UIactions.clear(versionColumnID));
      dispatch(UIactions.clear(connectionColumnID));
      dispatch(UIactions.clear(stateColumnID));
      dispatch(warehouseActions.unload(devicesDataId));
      dispatch(warehouseActions.unload(usersListId));
      dispatch(workerActions.clear(devicesLoadedId));
      dispatch(workerActions.clear(requestLogsId));
      dispatch(workerActions.clear(forceRescanId));
    };
  }, [dispatch]);

  return (
    <Core
      {...{
        devices: paginatedDevices,
        allDevices,
        done,
        working,
        error,
        workerId: devicesLoadedId,
      }}
    />
  );
};

export default Reports;
